import {
  addDays,
  getDay,
  getWeek,
  setDay,
  setWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import type { DateRange } from '../../types';
import { isDateDisabled } from '../../validation';
import type {
  ComparisonPresetDefinition,
  DailyComparisonPresetId,
  DailyPresetId,
  MainPresetDefinition,
} from '../types';

export const DAILY_PRESET_REGISTRY: Record<DailyPresetId, MainPresetDefinition<DailyPresetId>> = {
  custom: {
    id: 'custom',
    labelKey: 'datepicker.options.main.custom',
    calculate: () => undefined,
  },
  no_selection: {
    id: 'no_selection',
    labelKey: 'datepicker.options.main.no.selection',
    calculate: () => undefined,
  },
  yesterday: {
    id: 'yesterday',
    labelKey: 'datepicker.options.main.yesterday',
    calculate: today => {
      const yesterday = subDays(today, 1);
      return { from: yesterday, to: yesterday };
    },
  },
  last_7_days: {
    id: 'last_7_days',
    labelKey: 'datepicker.options.main.last.seven',
    calculate: today => ({ from: subDays(today, 7), to: subDays(today, 1) }),
  },
  last_14_days: {
    id: 'last_14_days',
    labelKey: 'datepicker.options.main.last.fourteen',
    calculate: today => ({ from: subDays(today, 14), to: subDays(today, 1) }),
  },
  last_30_days: {
    id: 'last_30_days',
    labelKey: 'datepicker.options.main.last.thirty',
    calculate: today => ({ from: subDays(today, 30), to: subDays(today, 1) }),
  },
  last_6_months: {
    id: 'last_6_months',
    labelKey: 'datepicker.options.main.last.six.months',
    calculate: today => ({ from: subMonths(today, 6), to: subDays(today, 1) }),
  },
  last_year: {
    id: 'last_year',
    labelKey: 'datepicker.options.main.last.year',
    calculate: today => ({ from: subYears(today, 1), to: subDays(today, 1) }),
  },
  this_month: {
    id: 'this_month',
    labelKey: 'datepicker.options.main.this.month',
    calculate: today => ({ from: startOfMonth(today), to: subDays(today, 1) }),
  },
  this_quarter: {
    id: 'this_quarter',
    labelKey: 'datepicker.options.main.this.quarter',
    calculate: today => ({ from: startOfQuarter(today), to: subDays(today, 1) }),
  },
  this_year: {
    id: 'this_year',
    labelKey: 'datepicker.options.main.this.year',
    calculate: today => ({ from: startOfYear(today), to: subDays(today, 1) }),
  },
};

const withValidFlag = (from: Date | undefined, to: Date | undefined): { from: Date; to: Date } | undefined => {
  if (!from || !to || isDateDisabled(from) || isDateDisabled(to)) return undefined;
  return { from, to };
};

export const DAILY_COMPARISON_REGISTRY: Record<
  DailyComparisonPresetId,
  ComparisonPresetDefinition<DailyComparisonPresetId>
> = {
  no_comparison: {
    id: 'no_comparison',
    labelKey: 'datepicker.options.comparison.none',
    calculate: () => undefined,
  },
  custom: {
    id: 'custom',
    labelKey: 'datepicker.options.main.custom',
    calculate: (_today, mainRange: DateRange) => {
      if (!mainRange.from || !mainRange.to) return undefined;
      const fromDate = new Date(mainRange.from);
      const daysDiff = Math.abs(Math.round((new Date(mainRange.to).getTime() - fromDate.getTime()) / 86_400_000)) + 1;
      const week = getWeek(fromDate);
      const dayInWeek = getDay(fromDate);
      const startDate = setDay(setWeek(subYears(fromDate, 1), week), dayInWeek);
      return withValidFlag(startDate, addDays(startDate, daysDiff - 1));
    },
  },
  previous_period: {
    id: 'previous_period',
    labelKey: 'datepicker.options.comparison.previous.period',
    calculate: (_today, mainRange: DateRange) => {
      if (!mainRange.from || !mainRange.to) return undefined;
      const fromDate = new Date(mainRange.from);
      const toDate = new Date(mainRange.to);
      const daysDiff = Math.abs(Math.round((toDate.getTime() - fromDate.getTime()) / 86_400_000)) + 1;
      return withValidFlag(subDays(fromDate, daysDiff), subDays(toDate, daysDiff));
    },
  },
  previous_year_by_date: {
    id: 'previous_year_by_date',
    labelKey: 'datepicker.options.comparison.previous.year.date',
    calculate: (_today, mainRange: DateRange) => {
      if (!mainRange.from || !mainRange.to) return undefined;
      return withValidFlag(subYears(new Date(mainRange.from), 1), subYears(new Date(mainRange.to), 1));
    },
  },
  previous_year_by_cal_week: {
    id: 'previous_year_by_cal_week',
    labelKey: 'datepicker.options.comparison.previous.year.week',
    calculate: (_today, mainRange: DateRange) => {
      if (!mainRange.from || !mainRange.to) return undefined;
      const fromDate = new Date(mainRange.from);
      const toDate = new Date(mainRange.to);
      const daysDiff = Math.abs(Math.round((toDate.getTime() - fromDate.getTime()) / 86_400_000)) + 1;
      const week = getWeek(fromDate);
      const dayInWeek = getDay(fromDate);
      const startDate = setDay(setWeek(subYears(fromDate, 1), week), dayInWeek);
      return withValidFlag(startDate, addDays(startDate, daysDiff - 1));
    },
  },
};
